/**
 * Reserve Terms Handler
 * 
 * This module handles the dynamic rendering of reserve policy terms
 * for cart and wishlist drawers when pre-sale items are present.
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: This class only handles reserve terms rendering
 * - Open/Closed: Extensible for different types of containers
 * - Dependency Inversion: Depends on abstractions (data attributes) not concrete implementations
 */

class ReserveTermsHandler {
  /**
   * Initialize the reserve terms handler
   * @param {Object} options - Configuration options
   * @param {string} options.selector - CSS selector for reserve terms elements
   * @param {string} options.presaleDataAttribute - Data attribute for presale detection
   */
  constructor(options = {}) {
    this.selector = options.selector || '[data-reserve-terms]';
    this.presaleDataAttribute = options.presaleDataAttribute || 'data-has-presale';
    this.init();
  }

  /**
   * Initialize the handler and start observing DOM changes
   */
  init() {
    this.processExistingElements();
    this.setupMutationObserver();
  }

  /**
   * Set up mutation observer to watch for DOM changes
   * This allows real-time detection of new elements being added
   */
  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a reserve terms element
              if (node.matches && node.matches(this.selector)) {
                this.processElement(node);
              }
              // Check child elements
              const childElements = node.querySelectorAll ? node.querySelectorAll(this.selector) : [];
              childElements.forEach(element => this.processElement(element));
            }
          });
        }
      });
    });

    // Start observing the entire document for changes
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process existing elements that are already in the DOM
   */
  processExistingElements() {
    const elements = document.querySelectorAll(this.selector);
    elements.forEach(element => {
      this.processElement(element);
    });
  }

  /**
   * Process a single element and render reserve terms if needed
   * @param {Element} element - The element to process
   */
  processElement(element) {
    if (!element || !element.matches(this.selector)) {
      return;
    }

    // Find the closest parent container with data-has-presale attribute
    const container = element.closest(`[${this.presaleDataAttribute}]`);
    const hasPresale = container ? container.getAttribute(this.presaleDataAttribute) === 'true' : false;
    
    if (hasPresale) {
      this.showReserveTerms(element);
    } else {
      this.hideReserveTerms(element);
    }
  }

  /**
   * Show reserve terms for the given element
   * @param {Element} element - The element to show terms for
   */
  showReserveTerms(element) {
    // Get the escaped text from the data attribute
    const textSpan = element.querySelector('[data-reserve-policy-text]');
    if (textSpan) {
      const escapedText = textSpan.getAttribute('data-reserve-policy-text');
      if (escapedText) {
        // Safely decode the HTML entities
        const decodedText = this.decodeHtmlEntities(escapedText);
        element.innerHTML = decodedText;
      }
    }
    
    // Show the element
    element.style.display = 'block';
  }

  /**
   * Safely decode HTML entities without executing code
   * @param {string} text - The escaped text
   * @returns {string} - The decoded text
   */
  decodeHtmlEntities(text) {
    const entities = {
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&amp;': '&'
    };
    
    return text.replace(/&lt;|&gt;|&quot;|&#39;|&amp;/g, match => entities[match]);
  }

  /**
   * Hide reserve terms for the given element
   * @param {Element} element - The element to hide terms for
   */
  hideReserveTerms(element) {
    // Simply hide the element
    element.style.display = 'none';
  }

  /**
   * Update the presale status for a specific container
   * @param {Element} container - The container element
   * @param {boolean} hasPresale - Whether the container has presale items
   */
  updatePresaleStatus(container, hasPresale) {
    if (!container) return;

    // Update the data attribute
    container.setAttribute(this.presaleDataAttribute, hasPresale.toString());
    
    // Find and process reserve terms elements within this container
    const reserveTermsElements = container.querySelectorAll(this.selector);
    reserveTermsElements.forEach(element => {
      this.processElement(element);
    });
  }

  /**
   * Clean up the observer when no longer needed
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

/**
 * Initialize reserve terms handler when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  // Create global instance for easy access
  window.reserveTermsHandler = new ReserveTermsHandler();
});

/**
 * Export for module usage if needed
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ReserveTermsHandler;
} 